import React from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
  Button,
  Tooltip,
  TextField
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import PropTypes from 'prop-types'

const getStatusChipStyle = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'sent':
      return { backgroundColor: '#e8f5e9', color: '#2e7d32' }
    case 'pending':
      return { backgroundColor: '#e3f2fd', color: '#1565c0' }
    case 'skipped':
      return { backgroundColor: '#fff3e0', color: '#ef6c00' }
    case 'failed':
    case 'error':
      return { backgroundColor: '#ffebee', color: '#c62828' }
    default:
      return { backgroundColor: '#eceff1', color: '#455a64' }
  }
}

const SmsHistoryTab = ({
  history,
  meta,
  onPageChange,
  loading,
  automations,
  selectedAutomationId,
  onAutomationChange
}) => {
  const selectedAutomation = automations.find(a => a.id === selectedAutomationId) || null

  const formatDate = (dateStr) => {
    const d = new Date(dateStr)
    return d.toLocaleString()
  }

  const truncateMessage = (msg, maxLen = 60) => {
    if (!msg || msg.length <= maxLen) return msg
    return msg.substring(0, maxLen) + '...'
  }

  if (automations.length === 0) {
    return (
      <Box textAlign="center" p={4}>
        <Typography variant="body1" color="textSecondary">
          No SMS automations configured yet.
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
          Create an automation first, then send history will appear here.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box mb={2} maxWidth={400}>
        <Autocomplete
          options={automations}
          getOptionLabel={(option) => option.name || `Automation #${option.id}`}
          value={selectedAutomation}
          onChange={(e, newValue) => {
            if (newValue) onAutomationChange(newValue.id)
          }}
          renderInput={(params) => (
            <TextField {...params} label="Select Automation" variant="outlined" size="small" />
          )}
          disableClearable
        />
      </Box>

      {!history || history.length === 0 ? (
        <Box textAlign="center" p={4}>
          <Typography variant="body1" color="textSecondary">
            No SMS messages have been sent yet for this automation.
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Campaign</TableCell>
                  <TableCell>To Number</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {formatDate(row.created_at)}
                    </TableCell>
                    <TableCell>{row.campaign_name || '-'}</TableCell>
                    <TableCell>{row.to_number}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status || 'unknown'}
                        size="small"
                        style={getStatusChipStyle(row.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.message_body || ''} placement="top-start">
                        <Typography variant="body2" style={{ cursor: 'default' }}>
                          {truncateMessage(row.message_body)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {meta && meta.totalPages > 1 && (
            <Box display="flex" justifyContent="center" alignItems="center" mt={2} style={{ gap: 16 }}>
              <Button
                size="small"
                disabled={meta.page <= 1 || loading}
                onClick={() => onPageChange(meta.page - 1)}
              >
                Previous
              </Button>
              <Typography variant="body2" color="textSecondary">
                Page {meta.page} of {meta.totalPages} ({meta.total} total)
              </Typography>
              <Button
                size="small"
                disabled={!meta.hasMore || loading}
                onClick={() => onPageChange(meta.page + 1)}
              >
                Next
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

SmsHistoryTab.propTypes = {
  history: PropTypes.array.isRequired,
  meta: PropTypes.shape({
    total: PropTypes.number,
    page: PropTypes.number,
    totalPages: PropTypes.number,
    hasMore: PropTypes.bool
  }),
  onPageChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  automations: PropTypes.array.isRequired,
  selectedAutomationId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  onAutomationChange: PropTypes.func.isRequired
}

export default SmsHistoryTab
